import { getRequest, postRequest, retryUntilSuccessOrFailure } from '../utils'

export interface IFCUploadResponse {
  task_id: string
  ifc_file_guid: string
}

export interface ParsedPlanarWallResponse {
  shape: { points: Array<{ x: number; y: number; z: number }> }
  guid: string
  placement?: string
  openings?: unknown[]
  domains?: unknown[]
}

export const uploadIFCFile: RequestTypeVariable = {
  getKey: (projectId: string) => ['uploadIFCFile', projectId],
  request: async (projectId: string, file: File): Promise<IFCUploadResponse> => {
    const formData = new FormData()
    formData.append('upload', file)

    const { data } = await postRequest<IFCUploadResponse>({
      url: `/projects/${projectId}/ifc/upload`,
      data: formData,
    })

    return data as IFCUploadResponse
  },
}

export const parseWall: RequestTypeVariable = {
  getKey: (projectId: string, ifcFileGuid: string, elementGuid: string) => [
    'parseWall',
    projectId,
    ifcFileGuid,
    elementGuid,
  ],
  request: async (
    projectId: string,
    ifcFileGuid: string,
    elementGuid: string,
  ): Promise<ParsedPlanarWallResponse> => {
    const { data } = await getRequest<ParsedPlanarWallResponse>({
      url: `/projects/${projectId}/parse-wall/${elementGuid}`,
      params: { ifc_file_guid: ifcFileGuid },
    })
    return data as ParsedPlanarWallResponse
  },
}

export type StoreyAssignmentStrategy =
  | {
      storeyDetectStrategyType: 'auto-detect-storey'
      minMatchLength: number
      allowMultipleMatches: boolean
    }
  | { storeyDetectStrategyType: 'provide-target-storey'; storey: number }

export type StoreyDetectStrategyType = StoreyAssignmentStrategy['storeyDetectStrategyType']

/** Wall placement: `Internal` = inner wall, `External` = outer wall (matches the backend enum). */
export type WallPlacement = 'Internal' | 'External'

export interface IFCWallParseConfig {
  ifc_element_id: string
  ifc_file_id: string
  parsing_target_type: 'wall' | 'opening'
  storeyAssignmentStrategy: StoreyAssignmentStrategy
  parseOpenings?: boolean
  enforceCartesianAlignment?: boolean
  limitOpeningXYCoords?: boolean
  placement?: WallPlacement
  shortenWallOnImport?: boolean
  maxShorteningDistance?: number
  handleDuplicateWallConflicts?: boolean
  minOverlapForDuplicateDetection?: number
}

/** Body of a successful `/task-status/{id}`; `result` is the task's report, when small. */
interface TaskStatusResponse<T> {
  msg: string
  result?: T
}

/** Report of the IFC wall parse task: what was created and what not, and why. */
export interface IFCWallParseReport {
  created: { ifc_element_id: string; storey: string; wall_guid: string }[]
  skipped: {
    ifc_element_id: string
    storey?: string
    reason: 'no_matching_storey' | 'already_imported' | 'duplicate_of_existing_wall'
    existing_wall_guid?: string
  }[]
  openings_added: number
  openings_failed: { ifc_element_id: string; wall_guid: string; error: string }[]
}

/** Report of the IFC opening parse task. */
export interface IFCOpeningParseReport {
  added: { ifc_element_id: string; wall_guid: string }[]
  skipped: {
    ifc_element_id: string
    wall_guid: string
    reason: 'already_imported' | 'no_opening_in_wall_plane'
  }[]
  failed: { ifc_element_id: string; wall_guid: string; error: string }[]
}

/** Report of the IFC roof slab parse task. */
export interface IFCRoofSlabParseReport {
  created: { ifc_element_id: string; roof_slab_guids: string[] }[]
  empty: string[]
  failed: { ifc_element_id: string; error: string }[]
}

/** Polls a parse task and returns its report (undefined if the backend sent none). */
const awaitParseTaskReport = async <T>(taskId: string | undefined): Promise<T | undefined> => {
  const response = await retryUntilSuccessOrFailure<TaskStatusResponse<T>>({
    url: `/task-status/${taskId}`,
  })
  return response.data?.result
}

export const startIfcWallParse: RequestTypeVariable = {
  getKey: (projectId: string) => ['startIfcWallParse', projectId],
  request: async (
    projectId: string,
    configs: IFCWallParseConfig[],
  ): Promise<IFCWallParseReport | undefined> => {
    if (!configs.length) return undefined
    const { data } = await postRequest<{ task_id: string }>({
      url: `/projects/${projectId}/planar-model/ifc-parse/wall`,
      data: configs,
    })

    return awaitParseTaskReport<IFCWallParseReport>(data?.task_id)
  },
}

export interface IFCOpeningParseConfig {
  ifc_element_id: string
  ifc_file_id: string
  parsedWallId: string
  limitOpeningXYCoords?: boolean
}

export const startIfcOpeningParse: RequestTypeVariable = {
  getKey: (projectId: string) => ['startIfcOpeningParse', projectId],
  request: async (
    projectId: string,
    configs: IFCOpeningParseConfig[],
  ): Promise<IFCOpeningParseReport | undefined> => {
    if (!configs.length) return undefined
    const { data } = await postRequest<{ task_id: string }>({
      url: `/projects/${projectId}/planar-model/ifc-parse/opening`,
      data: configs,
    })

    return awaitParseTaskReport<IFCOpeningParseReport>(data?.task_id)
  },
}

export interface IFCRoofSlabParseConfig {
  ifc_element_id: string
  ifc_file_id: string
}

export const startIfcRoofSlabParse: RequestTypeVariable = {
  getKey: (projectId: string) => ['startIfcRoofSlabParse', projectId],
  request: async (
    projectId: string,
    configs: IFCRoofSlabParseConfig[],
  ): Promise<IFCRoofSlabParseReport | undefined> => {
    if (!configs.length) return undefined
    const { data } = await postRequest<{ task_id: string }>({
      url: `/projects/${projectId}/planar-model/ifc-parse/roof-slab`,
      data: configs,
    })

    return awaitParseTaskReport<IFCRoofSlabParseReport>(data?.task_id)
  },
}
